import { Link } from 'react-router';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { DeviceReadiness } from '../hooks/useDeviceDetails';

/**
 * Renders an actionable banner when a device isn't fully serviceable yet — no or
 * offline gateway, a credential whose secret never reached a gateway, or nothing
 * that will poll it. Renders nothing when the device is monitored (or when
 * there's no readiness info).
 *
 * A dead credential (`undeployed`) is a hard problem (the secret is lost and must
 * be re-created), so it's shown as an error; everything else is a warning.
 */
export default function DeviceReadinessBanner({
  readiness,
  className,
}: {
  readiness?: DeviceReadiness | null;
  className?: string;
}) {
  if (!readiness || readiness.monitored || !readiness.message) return null;

  const dead = readiness.credentials === 'undeployed';
  // A missing/offline gateway is the actionable lever for the non-dead cases.
  const gatewayLink = readiness.gateway === 'none' || readiness.gateway === 'offline';
  // Everything upstream is healthy and still nothing polls this device — a
  // different problem from "not set up yet", so it gets its own title. The
  // backend only emits this message once the gateway and credential axes are
  // clean, so it can't collide with `gatewayLink`.
  const nothingPolling = readiness.polling === 'none' && !gatewayLink && !dead;

  return (
    <Alert variant={dead ? 'destructive' : 'default'} className={className}>
      {dead ? <AlertCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
      <AlertTitle>
        {dead
          ? 'This device can’t be monitored'
          : nothingPolling
            ? 'Nothing is polling this device'
            : 'Not being monitored yet'}
      </AlertTitle>
      <AlertDescription>
        {readiness.message}
        {gatewayLink ? (
          <>
            {' '}
            <Link to="/gateways" className="font-medium underline underline-offset-2">
              Manage gateways
            </Link>
          </>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}
